import { Link, useLocation } from 'react-router-dom';
import { CircleUser } from 'lucide-react';
import ziplinkLogo from '@/assets/ziplink-logo.png';

/**
 * Header — full-width glass bar pinned flush to the top edge.
 *
 * The bar spans the viewport and sits at top:0 with square corners; its
 * glass fills up behind the status bar / notch (safe-area) so it reads as
 * "attached all the way up". It stays fixed on scroll. Content is capped
 * to a comfortable max width and centred on wide screens.
 *
 * Layout:
 *   - Left:   ZipLink logo (black/white wordmark, auto-inverts via difference blend).
 *   - Center: contextual page title (e.g. "Nycklar", "Enheter").
 *   - Right:  profile icon link.
 *
 * The ZipLink logo on the left auto-inverts against the bar via
 * `brightness(0) invert(1)` (forcing pure white) combined with
 * `mix-blend-mode: difference` — white differenced against a dark surface
 * renders white, against a light surface renders black.
 */

const PAGE_TITLES: Array<{ match: (p: string) => boolean; title: string }> = [
  { match: (p) => p === '/', title: 'Hem' },
  { match: (p) => p === '/keys', title: 'Nycklar' },
  { match: (p) => p === '/bookings', title: 'Bokningar' },
  { match: (p) => p.startsWith('/book/'), title: 'Boka resurs' },
  { match: (p) => p === '/history', title: 'Historik' },
  { match: (p) => p === '/profile', title: 'Profil' },
  { match: (p) => p === '/send-key', title: 'Tilldela nyckel' },
  { match: (p) => p.startsWith('/admin/arenden'), title: 'Ärendehantering' },
  { match: (p) => p.startsWith('/admin/devices'), title: 'Enheter (admin)' },
  { match: (p) => p.startsWith('/admin/users'), title: 'Användare (admin)' },
  { match: (p) => p.startsWith('/admin/keys'), title: 'Nycklar (admin)' },
  { match: (p) => p.startsWith('/admin'), title: 'Admin' },
];

function pageTitleFor(pathname: string): string | null {
  return PAGE_TITLES.find((p) => p.match(pathname))?.title ?? null;
}

export function Header() {
  const { pathname } = useLocation();
  const pageTitle = pageTitleFor(pathname);

  return (
    <>
      {/* Spacer — preserves layout flow since the bar is fixed */}
      <div
        aria-hidden="true"
        className="h-[calc(56px+env(safe-area-inset-top))]"
      />

      {/* Full-width glass bar — flush to the top edge (glass fills up behind
          the status bar / notch), square corners, pinned on scroll. */}
      <header
        className="fixed inset-x-0 top-0 z-40 glass-thin border-x-0 border-t-0"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="relative mx-auto flex h-14 max-w-[720px] items-center justify-between px-4">
          {/* Left: ZipLink logo (auto-inverts light/dark via difference blend) */}
          <div className="flex min-w-0 shrink items-center gap-2">
            <img
              src={ziplinkLogo}
              alt="ZipLink"
              className="h-6 w-auto max-w-[140px] object-contain"
              style={{
                // Force pure white silhouette, then difference-blend
                // against the backdrop for auto-contrast in light/dark.
                filter: 'brightness(0) invert(1)',
                mixBlendMode: 'difference',
              }}
            />
          </div>

          {/* Center: current page title */}
          {pageTitle && (
            <span className="absolute left-1/2 -translate-x-1/2 text-xs font-medium tracking-wide text-foreground/80">
              {pageTitle}
            </span>
          )}

          {/* Right: profile icon */}
          <Link
            to="/profile"
            aria-label="Profil"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--glass-stroke))] bg-[hsl(var(--foreground)/0.06)] transition-all hover:scale-105 active:scale-95"
          >
            <CircleUser className="h-4 w-4 text-foreground/85" />
          </Link>
        </div>
      </header>
    </>
  );
}
